import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Button,
  Snackbar,
  Tooltip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ScienceIcon from "@mui/icons-material/Science";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth0 } from "@auth0/auth0-react";
import { runSentrySelfTest } from "../utils/sentry";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

const SENTRY_DSN_CONFIGURED = Boolean(import.meta.env.VITE_SENTRY_DSN);

export default function AdminEnvVars() {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchEnv = React.useCallback(
    async (showRefresh = false) => {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        const response = await fetch(`${API_BASE_URL}/admin/env`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }
        const res = await response.json();
        setData(res);
      } catch (e) {
        setError(e.message || "Failed to fetch env vars");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAccessTokenSilently]
  );

  React.useEffect(() => {
    fetchEnv();
  }, [fetchEnv]);

  const handleRefresh = () => {
    fetchEnv(true);
  };

  const handleCopyValue = (key, value) => {
    navigator.clipboard.writeText(value);
  };

  const handleSentrySelfTest = () => {
    const result = runSentrySelfTest();
    if (!result.ok) {
      setSnackbar({
        open: true,
        message:
          "Sentry is disabled (no VITE_SENTRY_DSN). Set the DSN to run this test.",
        severity: "warning",
      });
      return;
    }
    setSnackbar({
      open: true,
      message:
        "Sentry self-test sent: log, error-level message, metrics, replay flush. Check Issues, Logs, Metrics, and Replay in Sentry.",
      severity: "success",
    });
  };

  const env = data?.env ?? {};
  const excludedStr = env.EXCLUDED_ENVS ?? '';
  const excluded = excludedStr ? excludedStr.split(', ') : [];
  const entries = Object.entries(env)
    .filter(([key]) => key !== 'EXCLUDED_ENVS')
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component="a"
                  href="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  API Environment
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="API Environment Variables"
              description="Non-Squarespace env vars (SECRET vars excluded). Confirm the API has the required configuration."
              icon={<SettingsIcon sx={{ color: "primary.main" }} />}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading || refreshing}
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>
                <Tooltip
                  title={
                    SENTRY_DSN_CONFIGURED
                      ? "Sends a log, an error-level message, a metric, and flushes Session Replay"
                      : "Set VITE_SENTRY_DSN to enable Sentry testing"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<ScienceIcon />}
                      onClick={handleSentrySelfTest}
                      disabled={!SENTRY_DSN_CONFIGURED}
                    >
                      Test Sentry
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading && !refreshing ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : data ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Card>
                <CardContent>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Variable</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                              No env variables found (non-uppercase, no underscore, or Squarespace excluded)
                            </TableCell>
                          </TableRow>
                        ) : (
                          entries.map(([key, value]) => (
                            <TableRow key={key} hover>
                              <TableCell
                                component="th"
                                scope="row"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {key}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.8rem",
                                  wordBreak: "break-all",
                                  maxWidth: 400,
                                }}
                              >
                                {value || <em>(empty)</em>}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyValue(key, value)}
                                  title="Copy value"
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {excluded.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      EXCLUDED_ENVS ({excluded.length} variables)
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {excluded.map((key) => (
                        <Chip
                          key={key}
                          label={key}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
              </Box>
            ) : null}
          </Container>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
